import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineDocument,
  HiOutlineDocumentText,
  HiOutlineArchiveBox,
  HiOutlinePaperClip,
  HiOutlineArrowDownTray,
  HiOutlinePlay,
  HiOutlineXMark,
} from 'react-icons/hi2';
import {
  r2Url,
  partitionAttachments,
  formatBytes,
  fileCategory,
} from '../../services/r2';
import './ChatAttachments.css';

// 카테고리 → SVG 아이콘 컴포넌트. 색상 구분은 .cat-* 클래스(CSS)로.
function FileCategoryIcon({ file }) {
  const cat = fileCategory(file);
  if (cat === 'zip') return <HiOutlineArchiveBox aria-hidden />;
  if (cat === 'text') return <HiOutlineDocumentText aria-hidden />;
  if (cat === 'file') return <HiOutlinePaperClip aria-hidden />;
  // pdf / word / excel / ppt — 문서 아이콘 공통, 색만 분기
  return <HiOutlineDocument aria-hidden />;
}

/*
  채팅 메시지 버블 안에 첨부를 렌더링.
  - 이미지: 인라인 썸네일 그리드 (클릭 → lightbox)
  - 동영상: 인라인 <video controls> (클릭 → lightbox)
  - 그 외 파일: 아이콘 + 이름 + 크기 + 다운로드 카드
*/

export default function ChatAttachments({ attachments, onOpenLightbox, onRemove }) {
  const { t } = useTranslation('chatAttachments');
  if (!attachments || attachments.length === 0) return null;
  const { images, videos, files } = partitionAttachments(attachments);
  // lightbox 대상 = 이미지 + 동영상만 (파일은 별도 카드)
  const lightboxItems = [...images, ...videos];

  const openAt = (item) => {
    const idx = lightboxItems.indexOf(item);
    if (idx >= 0 && onOpenLightbox) onOpenLightbox(lightboxItems, idx);
  };

  const removeBtn = (item) =>
    onRemove ? (
      <button
        type="button"
        className="claude-chat-attach-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.key);
        }}
        aria-label={t('removeAttachment')}
        title={t('remove')}
      >
        <HiOutlineXMark aria-hidden />
      </button>
    ) : null;

  return (
    <div className="claude-chat-attachments">
      {lightboxItems.length > 0 && (
        <div
          className={`claude-chat-attach-media claude-chat-attach-count-${Math.min(
            lightboxItems.length,
            4
          )}`}
        >
          {images.map((img, i) => (
            <span
              key={img.key || `img-${i}`}
              className="claude-chat-attach-media-cell"
            >
              <button
                type="button"
                className="claude-chat-attach-img-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAt(img);
                }}
                aria-label={img.name || t('viewImage')}
              >
                <img
                  src={r2Url(img.key)}
                  alt={img.name || ''}
                  loading="lazy"
                  decoding="async"
                />
              </button>
              {removeBtn(img)}
            </span>
          ))}
          {videos.map((v, i) => (
            <span
              key={v.key || `vid-${i}`}
              className="claude-chat-attach-media-cell"
            >
              <button
                type="button"
                className="claude-chat-attach-video-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAt(v);
                }}
                aria-label={v.name || t('playVideo')}
              >
                <video
                  src={r2Url(v.key)}
                  preload="metadata"
                  muted
                  playsInline
                  className="claude-chat-attach-video"
                />
                <span className="claude-chat-attach-video-play" aria-hidden>
                  <HiOutlinePlay />
                </span>
              </button>
              {removeBtn(v)}
            </span>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <ul className="claude-chat-attach-files">
          {files.map((f, i) => (
            <li
              key={f.key || `file-${i}`}
              className={`claude-chat-attach-file-card cat-${fileCategory(f)}`}
            >
              <span className="claude-chat-attach-file-icon" aria-hidden>
                <FileCategoryIcon file={f} />
              </span>
              <div className="claude-chat-attach-file-info">
                <div className="claude-chat-attach-file-name" title={f.name}>
                  {f.name || t('file')}
                </div>
                <div className="claude-chat-attach-file-meta">
                  {formatBytes(f.size)}
                </div>
              </div>
              <a
                href={r2Url(f.key)}
                target="_blank"
                rel="noopener noreferrer"
                download={f.name}
                className="claude-chat-attach-file-download"
                aria-label={t('downloadFile', { name: f.name || t('file') })}
                title={t('download')}
                onClick={(e) => e.stopPropagation()}
              >
                <HiOutlineArrowDownTray aria-hidden />
              </a>
              {removeBtn(f)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* 이미지·동영상 풀스크린 viewer. ESC/← → 키 지원. */
export function ChatLightbox({ items, index, onChangeIndex, onClose }) {
  const { t } = useTranslation('chatAttachments');
  const item = items?.[index];

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      } else if (e.key === 'ArrowLeft' && index > 0) {
        onChangeIndex?.(index - 1);
      } else if (e.key === 'ArrowRight' && index < (items?.length || 0) - 1) {
        onChangeIndex?.(index + 1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, index, items, onChangeIndex, onClose]);

  if (!item) return null;
  const isVideo = (item.type || '').toLowerCase().startsWith('video/');
  const hasPrev = index > 0;
  const hasNext = index < (items?.length || 0) - 1;

  return (
    <div
      className="claude-chat-lightbox-overlay"
      onClick={onClose}
      role="dialog"
      aria-label={t('attachmentPreview')}
    >
      <button
        type="button"
        className="claude-chat-lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        aria-label={t('close')}
      >
        ×
      </button>
      {hasPrev && (
        <button
          type="button"
          className="claude-chat-lightbox-nav prev"
          onClick={(e) => {
            e.stopPropagation();
            onChangeIndex?.(index - 1);
          }}
          aria-label={t('prev')}
        >
          ‹
        </button>
      )}
      <div
        className="claude-chat-lightbox-content"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            key={item.key}
            src={r2Url(item.key)}
            controls
            autoPlay
            className="claude-chat-lightbox-media"
          />
        ) : (
          <img
            key={item.key}
            src={r2Url(item.key)}
            alt={item.name || ''}
            className="claude-chat-lightbox-media"
          />
        )}
        <div className="claude-chat-lightbox-caption">
          <span className="claude-chat-lightbox-name">{item.name}</span>
          {items.length > 1 && (
            <span className="claude-chat-lightbox-counter">
              {index + 1} / {items.length}
            </span>
          )}
        </div>
      </div>
      {hasNext && (
        <button
          type="button"
          className="claude-chat-lightbox-nav next"
          onClick={(e) => {
            e.stopPropagation();
            onChangeIndex?.(index + 1);
          }}
          aria-label={t('next')}
        >
          ›
        </button>
      )}
    </div>
  );
}
